import { isMobile } from 'lib/device'
import api, {
    addUploadBreadcrumb,
    captureUploadFailure,
    isUploadCancellationError,
    reportUploadDiagnostics,
} from 'api/api'
import { UPLOAD_FAILED_MESSAGE, uploadFailureMessage } from 'api/uploadDiagnostics'
import * as cache from 'state/cache'
import { fileHandler as applyFileToDraft } from './uploader'
import { isImageFile } from './composerImages'
import { type CompletedAnswerMedia } from './answerSubmitMedia'
import { uploadProgressBeforeAcknowledgement } from './uploadProgress'
import { type UploadedSharedFile, uploadedShareDraftPatch } from 'utils/uploadedSharedFile'
import { type OperationTrace } from 'utils/operationDiagnostics'

export type UploadProgressState = {
    recording: number | null
    files: Record<string, number | null>
}

type AnswerId = string

type UploadManagerCallbacks = {
    getAnswerId: () => AnswerId | null | undefined
    setUploadProgress: (updater: (prev: UploadProgressState) => UploadProgressState) => void
    setWaitingForEmbeds: (waiting: boolean) => void
    setModelPreviewProgress?: (pct: number) => void
    onMediaUploaded?: (patch: Record<string, unknown>, answerId: AnswerId) => void
    onUploadError?: (message: string) => void
    startUploadDiagnostic?: (mediaKind: 'video' | 'file') => OperationTrace | null
}

const MODEL_FILE_REGEXP = /\.(obj|glb|gltf|stl)$/i
const VIDEO_PREVIEW_UPLOAD_FALLBACK_MS = 1000

const DEFAULT_PROGRESS_STATE: UploadProgressState = {
    recording: null,
    files: {},
}

function getUploadFileMetadata(file: File) {
    const ext = file.name?.split('.').pop()?.trim().toLowerCase()
    return {
        file_size: file.size,
        file_name_length: file.name?.length,
        file_extension: ext && ext !== file.name.toLowerCase() ? ext : undefined,
        content_type: file.type || 'application/octet-stream',
    }
}

function activeStorageBlobPreviewUrl(signedId: string | null, filename?: string | null) {
    if (!signedId) return null
    const baseUrl = typeof window !== 'undefined' && window.knovApiUrl ? window.knovApiUrl : ''
    const safeFilename = encodeURIComponent((filename || 'upload').split('/').pop() || 'upload')
    return `${baseUrl}/rails/active_storage/blobs/redirect/${encodeURIComponent(
        signedId,
    )}/${safeFilename}`
}

export default class AnswerUploadManager {
    private pendingVideoFile: File | null = null
    private pendingVideoUploadTimer: ReturnType<typeof setTimeout> | null = null
    private recordingCancelFns: Array<() => void> = []
    private fileCancelFns: Array<() => void> = []
    private uploadInFlight: boolean = false
    private inFlightFile: File | null = null
    private inFlightSince = 0
    private completedMedia: (CompletedAnswerMedia & { answerId: AnswerId }) | null = null
    private pendingVideoDiagnostic: OperationTrace | null = null
    private activeVideoDiagnostic: OperationTrace | null = null
    private activeFileDiagnostic: OperationTrace | null = null

    constructor(private callbacks: UploadManagerCallbacks) {}

    hasActiveUpload = (): boolean => {
        return this.uploadInFlight
    }

    getCompletedMedia = (answerIdOverride?: AnswerId | null): CompletedAnswerMedia | null => {
        const answerId = answerIdOverride ?? this.getAnswerId()
        if (!answerId || this.completedMedia?.answerId !== answerId) return null

        const { files, recording } = this.completedMedia
        if (!recording && !files?.length) return null
        return {
            ...(files?.length ? { files: [...files] } : {}),
            ...(recording ? { recording } : {}),
        }
    }

    handleFileSelection = (evOrFile: Event | File | Blob) => {
        const answerId = this.getAnswerId()
        if (!answerId) return { acceptedImages: [], rejectedImageCount: 0 }

        let pickedFiles: File[] = []
        if (evOrFile instanceof File) {
            pickedFiles = [evOrFile]
        } else if (evOrFile instanceof Blob) {
            pickedFiles = [
                new File([evOrFile], 'recording.webm', {
                    type: evOrFile.type || 'video/webm',
                }),
            ]
        } else {
            const inputEvent = evOrFile as Event & { target?: HTMLInputElement }
            pickedFiles = Array.from(inputEvent?.target?.files || [])
            if (pickedFiles.length > 0) {
                inputEvent.stopPropagation?.()
            }
        }
        if (pickedFiles.length === 0) {
            return { acceptedImages: [], rejectedImageCount: 0 }
        }

        const pickedImages = pickedFiles.filter(isImageFile)
        const picked = pickedFiles.find(file => !isImageFile(file)) || pickedImages[0]
        if (!picked) return { acceptedImages: [], rejectedImageCount: 0 }

        const acceptedImages: File[] = []
        let rejectedImageCount = 0
        if (pickedImages.length > 0) {
            pickedImages.forEach(image => {
                const result = applyFileToDraft(image, answerId)
                acceptedImages.push(...result.accepted)
                rejectedImageCount += result.rejectedCount
                if (!result.accepted.length) return

                addUploadBreadcrumb('upload_file_selected', {
                    answer_id: answerId,
                    ...getUploadFileMetadata(image),
                    is_video: false,
                    is_image: true,
                    is_model: false,
                })
            })

            if (pickedFiles.every(isImageFile)) {
                return { acceptedImages, rejectedImageCount }
            }
        }

        applyFileToDraft(picked, answerId)
        addUploadBreadcrumb('upload_file_selected', {
            answer_id: answerId,
            ...getUploadFileMetadata(picked),
            is_video: picked.type?.includes('video') || false,
            is_image: picked.type?.includes('image') || false,
            is_model:
                picked.type?.startsWith('model/') || MODEL_FILE_REGEXP.test(picked.name || ''),
        })
        if (picked.type?.startsWith('model/') || MODEL_FILE_REGEXP.test(picked.name || '')) {
            this.callbacks.setWaitingForEmbeds(true)
            this.callbacks.setModelPreviewProgress?.(0)
        }

        const isVideo = picked.type?.includes('video')
        const isImage = picked.type?.includes('image')
        let fileUploadPromise: Promise<string | null> | null = null
        if (isVideo) {
            this.cancelRecordingUpload({ clearProgress: false })
            this.pendingVideoDiagnostic = this.callbacks.startUploadDiagnostic?.('video') || null
            this.pendingVideoDiagnostic?.mark('upload_begin')
            this.updateUploadProgress(prev => ({
                ...prev,
                recording: 0,
            }))
            this.pendingVideoFile = picked
            this.setUploadInFlight(picked)
            this.schedulePendingVideoUpload(answerId)
        } else if (!isImage) {
            const fileKey = picked.name || 'file'
            this.cancelFileUpload(undefined, { clearProgress: false })
            this.updateUploadProgress(prev => ({
                ...prev,
                files: { ...prev.files, [fileKey]: 0 },
            }))
            this.setUploadInFlight(picked)
            fileUploadPromise = this.startFileUpload(picked, fileKey, answerId)
        }

        return { acceptedImages, rejectedImageCount, fileUploadPromise }
    }

    handleUploadedFile = (uploadedFile: UploadedSharedFile): void => {
        const answerId = this.getAnswerId()
        if (!answerId || !uploadedFile?.signedId) return

        const patch = uploadedShareDraftPatch(uploadedFile, window.knovApiUrl || '')
        cache.updateCachedAnswer(answerId, patch)
        this.callbacks.onMediaUploaded?.(patch, answerId)
    }

    startPendingVideoUpload = async (answerIdOverride?: AnswerId | null) => {
        this.clearPendingVideoUploadTimer()
        const file = this.pendingVideoFile
        if (!file) return
        const answerId = answerIdOverride ?? this.getAnswerId()
        if (!answerId) return
        this.pendingVideoFile = null
        const diagnostic = this.pendingVideoDiagnostic
        this.pendingVideoDiagnostic = null
        this.activeVideoDiagnostic = diagnostic
        this.updateUploadProgress(prev => ({
            ...prev,
            recording: 0,
        }))
        try {
            if (isMobile) await new Promise(res => setTimeout(res, 200))
            diagnostic?.mark('preupload_begin')
            const media = await api.preuploadAnswerMedia(
                { recording: file },
                pct =>
                    this.updateUploadProgress(prev => ({
                        ...prev,
                        recording: uploadProgressBeforeAcknowledgement(pct),
                    })),
                fn => this.recordingCancelFns.push(fn),
                {
                    answerId,
                    uploadKind: 'recording',
                    source: 'AnswerUploadManager.startPendingVideoUpload',
                },
            )
            diagnostic?.mark('preupload_end')
            if (media?.recording) {
                const patch = {
                    recording: media.recording,
                    recording_url: activeStorageBlobPreviewUrl(media.recording, file.name),
                    recording_content_type: file.type || 'video/mp4',
                }
                this.rememberCompletedMedia(answerId, { recording: media.recording })
                cache.updateCachedAnswer(answerId, patch)
                this.callbacks.onMediaUploaded?.(patch, answerId)
                this.recordingCancelFns = []
                this.setUploadInFlight(null)
                this.updateUploadProgress(prev => ({
                    ...prev,
                    recording: 100,
                }))
                diagnostic?.mark('upload_end')
            } else {
                diagnostic?.mark('failed')
                this.updateUploadProgress(prev => ({
                    ...prev,
                    recording: null,
                }))
                this.recordingCancelFns = []
                this.setUploadInFlight(null)
            }
        } catch (e) {
            diagnostic?.mark(isUploadCancellationError(e) ? 'cancelled' : 'failed')
            if (isUploadCancellationError(e)) {
                addUploadBreadcrumb('upload_recording_cancelled', {
                    answer_id: answerId,
                    upload_kind: 'recording',
                    upload_source: 'AnswerUploadManager.startPendingVideoUpload',
                    ...getUploadFileMetadata(file),
                })
            } else {
                captureUploadFailure(e, {
                    answer_id: answerId,
                    upload_kind: 'recording',
                    upload_source: 'AnswerUploadManager.startPendingVideoUpload',
                    ...getUploadFileMetadata(file),
                })
                this.handleUploadError(uploadFailureMessage(e))
            }
            this.updateUploadProgress(prev => ({
                ...prev,
                recording: null,
            }))
            this.recordingCancelFns = []
            this.setUploadInFlight(null)
        } finally {
            if (this.activeVideoDiagnostic === diagnostic) this.activeVideoDiagnostic = null
        }
    }

    cancelRecordingUpload = (
        options: { clearProgress?: boolean; skipCancelFns?: boolean } = {},
    ) => {
        this.pendingVideoDiagnostic?.mark('cancelled')
        this.activeVideoDiagnostic?.mark('cancelled')
        this.pendingVideoDiagnostic = null
        this.activeVideoDiagnostic = null
        this.clearPendingVideoUploadTimer()
        this.clearCompletedMedia('recording')
        const hasActive =
            !!this.pendingVideoFile || this.recordingCancelFns.some(fn => typeof fn === 'function')
        if (hasActive && !options.skipCancelFns) {
            try {
                this.recordingCancelFns.forEach(fn => fn && fn())
            } catch (e) {}
        }
        this.recordingCancelFns = []
        this.pendingVideoFile = null
        this.setUploadInFlight(null)
        if (options.clearProgress !== false) {
            this.updateUploadProgress(prev => ({
                ...prev,
                recording: null,
            }))
        }
    }

    cancelFileUpload = (
        fileKey?: string,
        options: { clearProgress?: boolean; skipCancelFns?: boolean } = {},
    ) => {
        this.activeFileDiagnostic?.mark('cancelled')
        this.activeFileDiagnostic = null
        this.clearCompletedMedia('files')
        const hasActive = this.fileCancelFns.some(fn => typeof fn === 'function')
        if (hasActive && !options.skipCancelFns) {
            try {
                this.fileCancelFns.forEach(fn => fn && fn())
            } catch (e) {}
        }
        this.fileCancelFns = []
        this.setUploadInFlight(null)
        if (options.clearProgress !== false) {
            this.updateUploadProgress(prev => {
                if (!fileKey) {
                    return { ...prev, files: {} }
                }
                const next = { ...prev.files }
                delete next[fileKey]
                return { ...prev, files: next }
            })
        }
    }

    dispose = () => {
        this.cancelRecordingUpload({ clearProgress: false })
        this.cancelFileUpload(undefined, { clearProgress: false })
        this.pendingVideoFile = null
        this.setUploadInFlight(null)
    }

    private async startFileUpload(file: File, fileKey: string, answerId: AnswerId) {
        const diagnostic = this.callbacks.startUploadDiagnostic?.('file') || null
        this.activeFileDiagnostic = diagnostic
        diagnostic?.mark('upload_begin')
        try {
            diagnostic?.mark('preupload_begin')
            const media = await api.preuploadAnswerMedia(
                { files: [file] },
                pct =>
                    this.updateUploadProgress(prev => ({
                        ...prev,
                        files: {
                            ...prev.files,
                            [fileKey]: uploadProgressBeforeAcknowledgement(pct),
                        },
                    })),
                fn => this.fileCancelFns.push(fn),
                {
                    answerId,
                    uploadKind: 'file',
                    source: 'AnswerUploadManager.startFileUpload',
                },
            )
            diagnostic?.mark('preupload_end')
            if (Array.isArray(media?.files)) {
                const signedId = media.files[0] || null
                const stableUrl = activeStorageBlobPreviewUrl(signedId, file.name)
                const patch = {
                    files: media.files,
                    ...(stableUrl
                        ? {
                              file_urls: [
                                  {
                                      url: stableUrl,
                                      file_url: stableUrl,
                                      name: file.name || 'file',
                                      content_type: file.type || 'application/octet-stream',
                                  },
                              ],
                          }
                        : {}),
                }
                this.rememberCompletedMedia(answerId, { files: media.files })
                cache.updateCachedAnswer(answerId, patch)
                this.callbacks.onMediaUploaded?.(patch, answerId)
                this.fileCancelFns = []
                this.setUploadInFlight(null)
                this.updateUploadProgress(prev => ({
                    ...prev,
                    files: { ...prev.files, [fileKey]: 100 },
                }))
                diagnostic?.mark(signedId ? 'upload_end' : 'failed')
                return signedId
            }
            diagnostic?.mark('failed')
        } catch (e) {
            diagnostic?.mark(isUploadCancellationError(e) ? 'cancelled' : 'failed')
            if (isUploadCancellationError(e)) {
                addUploadBreadcrumb('upload_file_cancelled', {
                    answer_id: answerId,
                    upload_kind: 'file',
                    upload_source: 'AnswerUploadManager.startFileUpload',
                    ...getUploadFileMetadata(file),
                })
            } else {
                captureUploadFailure(e, {
                    answer_id: answerId,
                    upload_kind: 'file',
                    upload_source: 'AnswerUploadManager.startFileUpload',
                    ...getUploadFileMetadata(file),
                })
                this.handleUploadError(uploadFailureMessage(e))
            }
            this.updateUploadProgress(prev => {
                const next = { ...prev.files }
                delete next[fileKey]
                return { ...prev, files: next }
            })
            this.fileCancelFns = []
            this.setUploadInFlight(null)
        } finally {
            if (this.activeFileDiagnostic === diagnostic) this.activeFileDiagnostic = null
        }
        return null
    }

    private schedulePendingVideoUpload(answerId: AnswerId) {
        this.clearPendingVideoUploadTimer()
        this.pendingVideoUploadTimer = setTimeout(() => {
            this.pendingVideoUploadTimer = null
            void this.startPendingVideoUpload(answerId)
        }, VIDEO_PREVIEW_UPLOAD_FALLBACK_MS)
    }

    private clearPendingVideoUploadTimer() {
        if (this.pendingVideoUploadTimer == null) return
        clearTimeout(this.pendingVideoUploadTimer)
        this.pendingVideoUploadTimer = null
    }

    private rememberCompletedMedia(answerId: AnswerId, patch: CompletedAnswerMedia) {
        const current = this.completedMedia?.answerId === answerId ? this.completedMedia : null
        this.completedMedia = {
            answerId,
            ...(current?.files?.length ? { files: current.files } : {}),
            ...(current?.recording ? { recording: current.recording } : {}),
            ...patch,
        }
    }

    private clearCompletedMedia(kind: keyof CompletedAnswerMedia) {
        if (!this.completedMedia) return
        const next = { ...this.completedMedia }
        delete next[kind]
        this.completedMedia = next.files?.length || next.recording ? next : null
    }

    private updateUploadProgress(updater: (prev: UploadProgressState) => UploadProgressState) {
        const { setUploadProgress } = this.callbacks
        setUploadProgress(prev => {
            const base = prev || DEFAULT_PROGRESS_STATE
            return updater({
                recording: base.recording,
                files: { ...base.files },
            })
        })
    }

    // Leaving the page kills an upload in progress (a 789 MB upload was lost to a
    // reload on 10/30 with nothing logged): ask first, and report it if it happens.
    private setUploadInFlight(file: File | null) {
        this.uploadInFlight = !!file
        if (file && file !== this.inFlightFile) this.inFlightSince = Date.now()
        this.inFlightFile = file
        if (typeof window === 'undefined') return
        if (file) {
            window.addEventListener('beforeunload', this.warnBeforeUnload)
            window.addEventListener('pagehide', this.reportUnloadDuringUpload)
        } else {
            window.removeEventListener('beforeunload', this.warnBeforeUnload)
            window.removeEventListener('pagehide', this.reportUnloadDuringUpload)
        }
    }

    private warnBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!this.uploadInFlight) return
        event.preventDefault()
        event.returnValue = ''
    }

    private reportUnloadDuringUpload = () => {
        const file = this.inFlightFile
        if (!this.uploadInFlight || !file) return
        reportUploadDiagnostics('page_unloaded', {
            reason: 'page_unloaded',
            ...getUploadFileMetadata(file),
            upload_age_s: Math.round((Date.now() - this.inFlightSince) / 1000),
        })
    }

    private handleUploadError(message: string = UPLOAD_FAILED_MESSAGE) {
        if (this.callbacks.onUploadError) {
            this.callbacks.onUploadError(message)
        } else if (typeof alert === 'function') {
            alert(message)
        }
    }

    private getAnswerId(): AnswerId | null {
        try {
            const id = this.callbacks.getAnswerId()
            if (typeof id === 'number') {
                return Number.isNaN(id) ? null : id
            }
            if (typeof id === 'string' && id.trim().length > 0) {
                return id
            }
            return null
        } catch (e) {
            return null
        }
    }
}
